/**
 * Data Service - Capa unificada para acceso a datos (IndexedDB + RTDB)
 * Proporciona API simple para componentes React con soporte offline-first
 */

import indexedDBService from './indexedDBService';
import rtdbSyncService from './rtdbSyncService';

class DataService {
    constructor() {
        this.initialized = false;
        this.initPromise = null;
        this.subscriptions = new Map();
    }

    // Inicializar servicios
    async init() {
        if (this.initialized) return;
        if (this.initPromise) return this.initPromise;

        this.initPromise = (async () => {
            try {
                await indexedDBService.init();
                // Verificar si hay datos en caché o hacer sync inicial
                const hasLocalData = await this.checkLocalData();
                if (!hasLocalData) {
                    console.log('[DataService] Sin datos locales - sincronizando...');
                    await rtdbSyncService.syncStaticData(true);
                } else {
                    console.log('[DataService] Datos locales encontrados');
                    // Sincronizar en background
                    rtdbSyncService.syncStaticData(false).catch(console.error);
                }
                // Iniciar sync periódico
                rtdbSyncService.startPeriodicSync();
                this.initialized = true;
            } catch (error) {
                console.error('[DataService] Error inicializando:', error);
                this.initialized = true; // Permitir uso offline
            }
        })();

        return this.initPromise;
    }

    async checkLocalData() {
        const areas = await indexedDBService.getAreas();
        return areas && areas.length > 0;
    }

    // ÁREAS
    async getAreas() {
        await this.init();
        const { data, source } = await rtdbSyncService.getAreas();
        return { areas: data, source };
    }

    async getAreaById(id) {
        const areas = await indexedDBService.getAreas();
        return areas.find(a => a.id === parseInt(id));
    }

    // TEMAS
    async getTemas() {
        await this.init();
        const { data, source } = await rtdbSyncService.getTemas();
        return { temas: data, source };
    }

    async getTemasByArea(areaId) {
        await this.init();
        const { data, source } = await rtdbSyncService.getTemasByArea(areaId);
        return { temas: data, source };
    }

    async getTemaById(id) {
        const temas = await indexedDBService.getTemas();
        return temas.find(t => t.id === parseInt(id));
    }

    // PREGUNTAS
    async getPreguntasPorTema(temaId) {
        await this.init();
        const { data, source } = await rtdbSyncService.getPreguntasPorTema(temaId);
        return { preguntas: data, source };
    }

    async getPreguntasByTema(temaId) {
        // Intentar nueva estructura primero
        let { preguntas, source } = await this.getPreguntasPorTema(temaId);
        
        // Fallback a legacy si no hay datos
        if (!preguntas || preguntas.length === 0) {
            const legacy = await indexedDBService.getPreguntasLegacy(temaId);
            if (legacy && legacy.length > 0) {
                preguntas = legacy;
                source = 'legacy';
            }
        }
        
        return { preguntas, source };
    }

    async getRandomPreguntas(temaId, count = 10) {
        const { preguntas } = await this.getPreguntasByTema(temaId);
        if (!preguntas || preguntas.length === 0) return [];
        
        // Mezclar y tomar count
        const shuffled = [...preguntas].sort(() => Math.random() - 0.5);
        return shuffled.slice(0, count);
    }

    // QUIZZES PERSONALIZADOS
    async getUserQuizzes(userId) {
        await this.init();
        return indexedDBService.getUserQuizzes(userId);
    }

    async saveUserQuiz(userId, quizData) {
        const quiz = {
            id: quizData.id || `quiz_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            userId,
            ...quizData,
            createdAt: quizData.createdAt || Date.now(),
            updatedAt: Date.now()
        };
        await indexedDBService.saveUserQuiz(quiz);
        return quiz;
    }

    async deleteUserQuiz(quizId) {
        await indexedDBService.deleteUserQuiz(quizId);
    }

    // PROGRESO DE USUARIO
    async getUserProgress(userId, temaId) {
        await this.init();
        return indexedDBService.getUserProgress(userId, temaId);
    }

    async saveUserProgress(userId, temaId, progressData) {
        const progress = {
            id: `progress_${userId}_${temaId}`,
            userId,
            temaId,
            ...progressData,
            updatedAt: Date.now()
        };
        await indexedDBService.saveUserProgress(progress);
        return progress;
    }

    // RESULTADOS DE QUIZ (offline-first)
    async saveQuizResult(userId, resultData) {
        const result = {
            userId,
            ...resultData,
            timestamp: resultData.timestamp || Date.now()
        };
        return indexedDBService.saveQuizResult(result);
    }

    async getUnsyncedQuizResults(userId) {
        return indexedDBService.getUnsyncedQuizResults(userId);
    }

    async markQuizResultSynced(id) {
        await indexedDBService.markQuizResultSynced(id);
    }

    // CONFIGURACIÓN
    async getSetting(key) {
        return indexedDBService.getSetting(key);
    }

    async setSetting(key, value) {
        return indexedDBService.setSetting(key, value);
    }

    // ESTADÍSTICAS
    async getEstadisticasArea(areaId) {
        const { temas } = await this.getTemasByArea(areaId);
        let totalPreguntas = 0;
        
        for (const tema of temas) {
            const { preguntas } = await this.getPreguntasByTema(tema.id);
            totalPreguntas += preguntas?.length || 0;
        }
        
        return {
            totalTemas: temas.length,
            totalPreguntas,
            temas: temas.map(tema => ({
                ...tema,
                totalPreguntas: 0 // Se calcularía bajo demanda
            }))
        };
    }

    async getEstadisticasGenerales() {
        const { areas } = await this.getAreas();
        const { temas } = await this.getTemas();
        
        let totalPreguntas = 0;
        for (const tema of temas) {
            const { preguntas } = await this.getPreguntasByTema(tema.id);
            totalPreguntas += preguntas?.length || 0;
        }
        
        return {
            totalAreas: areas.length,
            totalTemas: temas.length,
            totalPreguntas,
            areas: areas.map(area => ({
                ...area,
                totalTemas: temas.filter(t => t.area_id === area.id).length,
                totalPreguntas: 0 // Calcular bajo demanda
            }))
        };
    }

    // SUSCRIPCIONES EN TIEMPO REAL
    subscribe(path, callback, options = {}) {
        const key = `${path}_${Date.now()}`;
        const unsubscribe = rtdbSyncService.subscribeToPath(path, callback, options);
        this.subscriptions.set(key, unsubscribe);
        return () => {
            unsubscribe();
            this.subscriptions.delete(key);
        };
    }

    unsubscribeAll() {
        for (const unsubscribe of this.subscriptions.values()) {
            unsubscribe();
        }
        this.subscriptions.clear();
        rtdbSyncService.unsubscribeAll();
    }

    // SINCRONIZACIÓN MANUAL
    async forceSync() {
        await rtdbSyncService.forceFullSync();
    }

    async syncPending() {
        await rtdbSyncService.syncAllPending();
    }

    // ESTADO
    getStatus() {
        return {
            initialized: this.initialized,
            isOnline: rtdbSyncService.isOnline,
            ...rtdbSyncService.getSyncStatus()
        };
    }

    // LIMPIEZA
    async clearAllData() {
        await indexedDBService.clearAllData();
        this.initialized = false;
        this.initPromise = null;
    }

    async close() {
        this.unsubscribeAll();
        rtdbSyncService.stopPeriodicSync();
        await indexedDBService.close();
    }
}

// Singleton
const dataService = new DataService();

export default dataService;